<template>
  <div class="space-y-4">
    <section v-if="hasBusinessBlock" class="space-y-2" data-test="business-alerts">
      <h3 class="text-sm font-semibold text-gray-700">{{ t("business_title") }}</h3>
      <Alert v-for="alert in businessAlerts" :key="alert.key" :type="alert.severity">
        <template v-if="alert.severity === 'warning'" #icon>
          <ExclamationCircleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </template>
        {{ alertMessage(alert) }}
      </Alert>
      <LcbftAlert v-if="customer" :lcbftScore="customer.lcbftScore" :lcbftRegistryId="customer.cbftRegistryId" />
    </section>
    <section v-if="hasFraudBlock" class="space-y-2" data-test="fraud-alerts">
      <h3 class="text-sm font-semibold text-gray-700">{{ t("fraud_title") }}</h3>
      <Alert v-if="claim.fraudSuspicion" type="error">
        <MarkdownText :content="fraudSuspicionMessage" />
      </Alert>
      <Alert v-for="alert in fraudAlerts" :key="alert.key" :type="alert.severity">
        <template v-if="alert.severity === 'warning'" #icon>
          <ExclamationCircleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </template>
        <div v-if="alert.key === 'fraud_reused_document'" class="space-y-2" data-test="reused-documents">
          <div
            v-for="section in reusedSections(alert.params.documents)"
            :key="section.name"
            :data-test="`reused-${section.name}`"
          >
            <p>{{ t(`fraud_reused_document_${section.name}`) }}</p>
            <ul class="mt-1 list-disc space-y-1 pl-5">
              <li v-for="file in section.files" :key="file.key" data-test="reused-file">
                « {{ file.filename }} » — {{ file.labels }}
                <ul>
                  <li v-for="match in file.matches" :key="match.reference" data-test="reused-match">
                    {{ match.reference }} : {{ match.labels }}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        <template v-else>{{ alertMessage(alert) }}</template>
      </Alert>
    </section>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { ExclamationCircleIcon } from "@heroicons/vue/solid";
import LcbftAlert from "@/components/plan_sale/LcbftAlert.vue";
import { Alert, MarkdownText } from "@estaly/ui";

export default {
  components: { Alert, LcbftAlert, MarkdownText, ExclamationCircleIcon },

  props: {
    claim: {
      type: Object,
      required: true,
    },
    customer: {
      type: Object,
      required: false,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    alerts() {
      return this.claim.alerts || [];
    },

    businessAlerts() {
      return this.alerts.filter((alert) => alert.category !== "fraud");
    },

    fraudAlerts() {
      return this.alerts.filter((alert) => alert.category === "fraud");
    },

    hasBusinessBlock() {
      return this.businessAlerts.length > 0 || Boolean(this.customer?.lcbftScore);
    },

    hasFraudBlock() {
      return this.fraudAlerts.length > 0 || Boolean(this.claim.fraudSuspicion);
    },

    fraudSuspicionMessage() {
      const reason = this.claim.fraudSuspicionReason;
      if (!this.claim.fraudSuspicionFlaggedBy) return this.t("fraud_suspicion", { reason });

      return this.t("fraud_suspicion_with_origin", {
        reason,
        author: this.claim.fraudSuspicionFlaggedBy,
        date: this.claim.fraudSuspicionFlaggedAt,
      });
    },
  },

  methods: {
    alertMessage(alert) {
      if (alert.key === "claim_type_not_covered") {
        return this.t(alert.key, { claimType: this.t(`claim.claim_type.${this.claim.claimType}`) });
      }
      if (alert.params?.claims) {
        return this.t(alert.key, { claims: this.citedClaims(alert.params.claims) });
      }
      return this.t(alert.key, alert.params || {});
    },

    citedClaims(claims) {
      return claims.map((claim) => `${claim.reference} (${claim.date})`).join(", ");
    },

    // Other customers first, the stronger fraud signal; a section without match is left out.
    reusedSections(documents) {
      return [
        { name: "other_customers", sameCustomer: false },
        { name: "same_customer", sameCustomer: true },
      ]
        .map(({ name, sameCustomer }) => {
          const sectionDocuments = documents
            .map((cited) => ({
              ...cited,
              matches: cited.matches.filter((match) => Boolean(match.sameCustomer) === sameCustomer),
            }))
            .filter((cited) => cited.matches.length > 0);
          return { name, files: this.reusedFiles(sectionDocuments) };
        })
        .filter((section) => section.files.length > 0);
    },

    // One entry per file: the same file filed under several types of this claim, with the same
    // matches, is listed once with all its types.
    reusedFiles(documents) {
      const files = [];
      documents.forEach((cited) => {
        const matches = this.matchesByClaim(cited.matches);
        const key = `${cited.filename}|${JSON.stringify(matches)}`;
        const file = files.find((existing) => existing.key === key);
        if (file) {
          file.documents.push(cited.document);
        } else {
          files.push({ key, filename: cited.filename, documents: [cited.document], matches });
        }
      });
      return files.map((file) => ({ ...file, labels: this.documentLabels(file.documents) }));
    },

    matchesByClaim(matches) {
      const documentsByReference = {};
      matches.forEach((match) => {
        documentsByReference[match.reference] = [...(documentsByReference[match.reference] || []), match.document];
      });
      return Object.entries(documentsByReference).map(([reference, documents]) => ({
        reference,
        labels: this.documentLabels(documents),
      }));
    },

    documentLabels(documents) {
      return documents.map((document) => this.t(`claim.declaration_field.${document}`)).join(", ");
    },
  },
};
</script>

<i18n src="./ClaimAlerts.i18n.json"></i18n>
